import actionTypes from 'constants/action-types';
import { createRoutine } from 'redux-saga-routines';

/**
 * Resets channels and reductions object for a particular channel.
 *
 * @param {String} channel  channel to reset
 * @param {boolean} multipleSeries channel supports multiple series
 *
 * @return {Object}         action object
 */
export function clearChannel(channel, multipleSeries) {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.CLEAR_CHANNEL,
    payload: {
      channel,
      multipleSeries,
    },
  };
}

/**
 * Copies the content of the source channel to the target channel.
 *
 * @param {Object} source  the source channel to copy (including channelName and channelType)
 * @param {Object} target  the target channel to copy to (including channelName and channelType)
 *
 * @return {Object}        action object
 */
export function copyEncodedChannel(source, target) {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.COPY_ENCODED_CHANNEL,
    payload: { source, target },
  };
}

/**
 * Maps a MongoDB Schema field [1] to a Vega scales [2], such as
 * x, y, color, size, etc and stores it in the Vega scale domain's `field` key.
 *
 * @param {Object} source  the source field to map (including fields and fieldPath) [1]
 * @param {Object} target  the target channel to map to (including channelName and channelType) [2]
 *
 * @see [1] https://github.com/mongodb-js/mongodb-schema
 * @see [2] https://vega.github.io/vega/docs/scales/
 *
 * @return {Object} - action object
 */
export function mapFieldToChannel(source, target) {
  const { fieldPath } = source;
  const { channelName, channelType, fields } = target;

  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.MAP_FIELD_TO_CHANNEL,
    payload: { channelName, channelType, fieldPath, fields },
  };
}

/**
 * Encodes the aggregate function for a channel.
 *
 * @see https://vega.github.io/vega/docs/transforms/aggregate/
 *
 * @param {String} channel    the encoding channel
 * @param {String} aggregate  the aggregate function to apply
 *
 * @return {Object}           action object
 */
export function selectAggregate(channel, aggregate) {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.SELECT_AGGREGATE,
    payload: { channel, aggregate },
  };
}

/**
 * Changes the type of chart the user has selected for display.
 *
 * @param {String} chartType  The name of chart, e.g. 'Bar Chart' or 'Scatter Plot'
 *
 * @return {Object}           action object
 */
export function selectChartType(chartType) {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.SELECT_CHART_TYPE,
    payload: chartType,
  };
}

/**
 * Changes category of charts
 * @param {String} category - category name, e.g. 'bar'
 * @returns {Object} - redux action with category name as payload
 */
export function selectChartCategory(category) {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.SELECT_CHART_CATEGORY,
    payload: category,
  };
}

/**
 * Sets an array reduction, after a channel has been encoded,
 * e.g. by calling mapFieldToChannel.
 *
 * @param {String} channel   the encoding channel
 * @param {Object} index     The stage of the reduction pipeline to update,
 *                           where the 0-th index represents the outermost
 *                           reduction.
 * @param {string} type      the array reduction type, e.g. $unwind
 * @param {Object} args      Array of user-provided arguments to the
 *                           reduction, e.g. the index to $arrayElemAt
 *
 * @return {Object}          action object
 */
export function setArrayReduction(channel, index, type, args = []) {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.SET_ARRAY_REDUCTION,
    payload: { channel, index, type, arguments: args },
  };
}

/**
 * Sets the latitude and longitude array indexes,
 *
 * @param {String} channel   the encoding channel
 * @param {Int} indexLong    Longitude number
 * @param {Int} indexLat     Latitude number
 *
 * @return {Object}          action object
 */
export function setArrayCoordsIndex(channel, indexLong, indexLat) {
  return {
    type:
      actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.GEOSPATIAL
        .SET_ARRAY_COORDS_INDEX,
    payload: { channel, indexLong, indexLat },
  };
}

/**
 * Sets the continuous color legend for maps,
 *
 * @param {String} _id       the id of the component
 * @param {Int} min          the max legend value
 * @param {Int} max          the min legend value
 *
 * @return {Object}          action object
 */
export function setMapLegend(_id, min, max) {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.GEOSPATIAL.SET_MAP_LEGEND,
    payload: { _id, min, max },
  };
}

/**
 * Sets the zero state message of the chart
 *
 * @param {String} title    The zero state message title
 * @param {String} subtitle The optional zero state message subtitle
 *
 * @return {Object}          action object
 */
export function setZeroStateMessage(title, subtitle = null) {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.SET_ZERO_STATE_MESSAGE,
    payload: { title, subtitle },
  };
}

/**
 * Sets the temporal binning for a channel
 *
 * @param {String} channel       the encoding channel
 * @param {Boolean} isPeriodic   is the temporal bin periodic? (ie May 2018 vs May)
 * @param {String} temporalBin   the label of the temporal option, which is one of TEMPORAL_BINNING_TOKENS
 *
 * @return {Object}              action object
 */
export function setTemporalBinning(channel, isPeriodic, temporalBin) {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.SELECT_BINNING.TEMPORAL,
    payload: { channel, isPeriodic, temporalBin },
  };
}

/**
 * Sets the quantitative binning size for a channel
 *
 * @param {String} channel       the encoding channel
 * @param {Number} binSize       the size of the bin
 *
 * @return {Object}              action object
 */
export function setQuantitativeBinning(channel, binSize) {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.SELECT_BINNING.QUANTITATIVE,
    payload: { channel, binSize },
  };
}

/**
 * Update the sorting for an encoded channel.
 *
 * @param {String} channel the name of encoding channel
 * @param {String} axis the axis to sort on
 * @param {String} order asc or desc order
 *
 * @return {Object} sortChannel action object
 */
export function sortChannel(channel, axis, order) {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.SORT_CHANNEL,
    payload: { channel, axis, order },
  };
}

/**
 * Update the sorting for a column (Tables only)
 *
 * @param {Array} payload - an array of Column Sort Models from api.getSortModel()
 * @see https://www.ag-grid.com/javascript-grid-api/
 *
 * @returns {Object} sortColumn action object, the payload keyed by colId (ie: the channel)
 **/
export function sortColumn(payload) {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.TABLE.SORT_COLUMN,
    payload,
  };
}

/**
 * Resize a column's width
 * @param {Object} column - column object returned by Ag-Grid
 * @param {Number} width - width of column
 *
 * @returns {Object} resizeColumn action object, the payload has shape { column: 'foo', width: 42 }
 */
export function resizeColumn(column, width) {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.TABLE.RESIZE_COLUMN,
    payload: { column, width },
  };
}

/**
 * Change a geospatial chart's viewport
 * @param {Object} center - center object returned by react-leaflet
 * @param {Number} zoom - zoom of chart
 *
 * @returns {Object} setViewport action object, the payload has shape { center: [lat, lng], zoom: 5 }
 */
export function setViewport(center, zoom) {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.GEOSPATIAL.SET_VIEWPORT,
    payload: { center, zoom },
  };
}

/**
 * Move an encoded channel to another location by position,
 * used for multi-channels
 *
 * @param {String} channel - Name of the channel to move
 * @param {Number} toIndex - Index to move the channel to
 * @returns {Object} moveChannel action object
 */
export function moveChannel(channel, toIndex) {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.MOVE_ENCODED_CHANNEL,
    payload: { channel, toIndex },
  };
}

/**
 * Update the limiting settings for an encoded channel.
 *
 * @param {String} channel the name of encoding channel
 * @param {Boolean} enabled whether limiting is enabled
 * @param {String} size number of results to show
 * @param {Boolean} fetchData whether limiting has been toggled
 *
 * @return {Object} limitSortableChannel action object
 */
export function limitSortableChannel(
  channel,
  enabled,
  size,
  fetchData = false
) {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.LIMIT_SORTABLE_CHANNEL,
    payload: { channel, enabled, size, fetchData },
  };
}

/**
 * Swaps the contents of two channels.
 *
 * @param {Object} source  the source channel to swap (including channelName and channelType)
 * @param {Object} target  the target channel to swap (including channelName and channelType)
 *
 * @return {Object}        action object
 */
export function swapEncodedChannels(source, target) {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.SWAP_ENCODED_CHANNEL,
    payload: { source, target },
  };
}

/**
 * Toggles the sampling stage on/off
 *
 * @param {Boolean} isSampling    indicate sampling is on if true, false otherwise
 *
 * @return {Object}        action object
 */
export function toggleSampling(isSampling) {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.TOGGLE_SAMPLING,
    payload: isSampling,
  };
}

/**
 * Sets the chartBuilder reducer's `queryCache.filter` value to the value of the query bar filter.
 * This does not automatically trigger a new sample.
 *
 * To apply the query you will also need to dispatch an `actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.APPLY_QUERY`
 * action to trigger a new aggregation pipeline for the chart.
 *
 * @param {String} queryFilter - the value of the QueryOptionFilter component
 *
 * @return {Object} - action object
 */
export function setQueryFilter(queryFilter) {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.QUERY.FILTER,
    payload: queryFilter,
  };
}

/**
 * Applies the query in the chartBuilder reducer's `queryCache` to the pipeline, triggering a request for
 * ITEM_DATA_CACHE.READ as a side-effect (see sagas/chart.js and sagas/item-data-cache.js)
 *
 * @return {Object} - action object
 */
export function applyQuery() {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.APPLY_QUERY,
  };
}

export function toggleBinning(channel, isBinning) {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.TOGGLE_BINNING,
    payload: { channel, isBinning },
  };
}

export function addFieldFilter(field) {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.FIELD_FILTER.ADD,
    payload: field,
  };
}

export function toggleFieldFilter(fieldPath, disabled) {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.FIELD_FILTER.TOGGLE,
    payload: { fieldPath, disabled },
  };
}

export function updateFieldFilter(fieldPath, settings) {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.FIELD_FILTER.UPDATE,
    payload: { fieldPath, settings },
  };
}

export function removeFieldFilter(field) {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.FIELD_FILTER.REMOVE,
    payload: field,
  };
}

/**
 * Action triggered when a new string typed field is added to fetch the distinct values of the field.
 */
export const fetchFilterValues = createRoutine(
  actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.FIELD_FILTER.FETCH_VALUES
);

/**
 * Set enabled state and value of the specified customisation option.
 *
 * @param {String}  option      name of the option to be updated
 * @param {Boolean} enabled     whether the option should be enabled
 * @param {String}  value       new value of the option
 * @param {String}  channel     channel name within the section
 * @param {String}  section     section this option belongs to
 *                              if not a generic option, axes or channels
 * @param {String}  multiSeries if this option belongs to a multi-series channel
 * @returns {Object} action object
 */
export function setCustomisationOption(
  option,
  enabled,
  value,
  channel,
  section,
  multiSeries
) {
  return {
    type: actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.SET_CUSTOMISATION,
    payload: { option, enabled, value, channel, section, multiSeries },
  };
}

export const fetchPipelineForChart = createRoutine(
  actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.FETCH_PIPELINE
);

export const fetchDataForDocView = createRoutine(
  actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.DOC_VIEW_DATA_CACHE
);

export const fetchDocumentsForChart = createRoutine(
  actionTypes.DASHBOARD_ITEMS.BUILDER.CHART.FETCH_DOCUMENTS
);

export default {
  applyQuery,
  addFieldFilter,
  toggleFieldFilter,
  updateFieldFilter,
  removeFieldFilter,
  fetchFilterValues,
  clearChannel,
  copyEncodedChannel,
  mapFieldToChannel,
  selectAggregate,
  selectChartType,
  setArrayReduction,
  setArrayCoordsIndex,
  setZeroStateMessage,
  setCustomisationOption,
  setMapLegend,
  setQuantitativeBinning,
  setQueryFilter,
  setTemporalBinning,
  sortChannel,
  sortColumn,
  resizeColumn,
  setViewport,
  limitSortableChannel,
  swapEncodedChannels,
  toggleSampling,
  toggleBinning,
  selectChartCategory,
};
